import { useEffect, useRef } from 'react'
import { useMap } from '@vis.gl/react-google-maps'

export interface HeatPoint {
  lat: number
  lng: number
  /** Intensidad del punto. Se normaliza contra `max`. */
  weight: number
}

export interface HeatLayerProps {
  points: HeatPoint[]
  /** Valor que corresponde a la intensidad máxima. Si se omite, el mayor peso presente. */
  max?: number
  /** Colores de menor a mayor intensidad. El primero puede ir transparente. */
  gradient: string[]
  /** Radio del punto más intenso, en metros. Los menos intensos se dibujan más pequeños. */
  radiusMeters: number
  opacity?: number
  visible?: boolean
  zIndex?: number
}

/**
 * Mapa de calor propio, sobre círculos ponderados.
 *
 * Google retiró `visualization.HeatmapLayer` en la versión 3.65 de la Maps
 * JavaScript API: el constructor sigue existiendo pero LANZA al instanciarse, así
 * que un guard de `typeof Ctor === 'function'` no basta — tumbaba la pantalla
 * entera. No hay reemplazo oficial, de modo que la capa se dibuja aquí.
 *
 * Diferencia con el heatmap original, que conviene tener presente: aquél
 * interpolaba densidad en un radio de PÍXELES, constante en pantalla a cualquier
 * zoom, y sumaba la contribución de puntos cercanos. Éste dibuja un círculo por
 * punto con radio en METROS, así que crece y decrece con el zoom como el terreno
 * que representa y no funde puntos vecinos en una mancha continua. Para leer
 * "dónde se concentra" —que es a lo que sirve la capa— se comporta igual, y a
 * cambio es honesto sobre la escala real de cada origen.
 */
export function HeatLayer({
  points,
  max,
  gradient,
  radiusMeters,
  opacity = 0.75,
  visible = true,
  zIndex,
}: HeatLayerProps) {
  const map = useMap()
  const circlesRef = useRef<google.maps.Circle[]>([])

  useEffect(() => {
    if (!map) return

    const clear = () => {
      for (const c of circlesRef.current) c.setMap(null)
      circlesRef.current = []
    }

    clear()
    if (!visible || points.length === 0) return

    const techo = max && max > 0
      ? max
      : points.reduce((acc, p) => Math.max(acc, p.weight), 0) || 1

    // De menor a mayor intensidad: los puntos calientes quedan dibujados encima,
    // que es donde debe ir la atención.
    const ordenados = [...points].sort((a, b) => a.weight - b.weight)

    for (const p of ordenados) {
      const t = Math.min(1, Math.max(0, p.weight / techo))
      const color = colorAt(gradient, t)

      // El radio no baja del 35% ni siquiera con peso cero: un origen registrado
      // existe aunque aporte poco, y un punto invisible se lee como "no hay nada".
      const radio = radiusMeters * (0.35 + 0.65 * t)

      const circle = new google.maps.Circle({
        center: { lat: p.lat, lng: p.lng },
        radius: radio,
        map,
        clickable: false,
        fillColor: color,
        fillOpacity: opacity * (0.45 + 0.55 * t),
        strokeColor: color,
        strokeOpacity: opacity * 0.5,
        strokeWeight: 1,
        zIndex,
      })
      circlesRef.current.push(circle)
    }

    return clear
  }, [map, points, max, gradient, radiusMeters, opacity, visible, zIndex])

  return null
}

/**
 * Color del gradiente en la posición `t` (0..1), interpolando en RGB entre los
 * dos tramos contiguos. Acepta "#rgb", "#rrggbb" y "rgba(r,g,b,a)" —el primer
 * color de un gradiente de calor suele venir transparente— y en ese caso ignora
 * el alfa, que aquí lo aporta `fillOpacity`.
 */
function colorAt(gradient: string[], t: number): string {
  if (gradient.length === 0) return '#c8442f'
  if (gradient.length === 1) return gradient[0]!

  const escala = t * (gradient.length - 1)
  const i = Math.min(gradient.length - 2, Math.floor(escala))
  const f = escala - i

  const a = parseColor(gradient[i]!)
  const b = parseColor(gradient[i + 1]!)
  if (!a || !b) return gradient[gradient.length - 1]!

  const mix = (x: number, y: number) => Math.round(x + (y - x) * f)
  return `rgb(${mix(a[0], b[0])}, ${mix(a[1], b[1])}, ${mix(a[2], b[2])})`
}

function parseColor(color: string): [number, number, number] | null {
  const rgba = color.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i)
  if (rgba) return [Number(rgba[1]), Number(rgba[2]), Number(rgba[3])]

  const hex = color.trim().replace('#', '')
  if (hex.length === 3) {
    const [r, g, b] = hex.split('')
    return [
      parseInt(r! + r!, 16),
      parseInt(g! + g!, 16),
      parseInt(b! + b!, 16),
    ]
  }
  if (hex.length === 6) {
    return [
      parseInt(hex.slice(0, 2), 16),
      parseInt(hex.slice(2, 4), 16),
      parseInt(hex.slice(4, 6), 16),
    ]
  }
  return null
}
